// Front-page "Advanced" search box (under #front-search): semantic-axis
// sliders, negative terms, and a classic-ranking escape hatch for the vector
// ranking that search.load now applies to text queries by default.
//
// State bridges into the legacy search machinery via hidden inputs that
// navigation.ts `search()` reads when building the lm_filters JSON item —
// values (not flags), so they survive parseFilters' String coercion server-side.
import { useState } from "preact/hooks";
import { getAction } from "../../actionRegistry";
import { PRESET_AXES } from "../labsSearch/SemanticMixer";

interface Axis {
  a: string;
  b: string;
  t: number;
}

const MAX_AXES = 5;

const CSS = `
.front-advanced { max-width: 720px; margin: 4px auto 0; text-align: left; }
/* Hidden by product decision. The island itself must stay mounted: its three
   hidden inputs (#lm_adv_negative/#lm_adv_axes/#lm_adv_classic) are read by
   navigation.ts search() on every query, and they are optional-chained there, so
   unmounting would silently drop the values rather than fail loudly. With no
   toggle the panel is unreachable, so those inputs just hold their defaults.
   To restore the feature, drop this display:none. */
.front-advanced .fa-toggle { display: none; font-size: 12px; color: #888;
  cursor: pointer; user-select: none; padding: 2px 6px; }
.front-advanced .fa-toggle:hover { color: #333; }
.front-advanced .fa-panel { border: 1px solid #e3e3e3; border-radius: 8px;
  padding: 10px 12px; margin-top: 4px; background: #fff; }
.front-advanced .fa-row { display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  margin-bottom: 8px; }
.front-advanced input[type="text"] { padding: 5px 8px; font-size: 13px;
  border: 1px solid #ccc; border-radius: 5px; }
.front-advanced .fa-neg { flex: 1 1 220px; max-width: 340px; }
.front-advanced .fa-chip { display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 9px; border: 1px solid #ccc; border-radius: 12px; cursor: pointer;
  user-select: none; font-size: 12px; color: #555; }
.front-advanced .fa-chip:hover { border-color: #333; color: #111; }
.front-advanced .fa-axis { display: flex; gap: 6px; align-items: center; margin-bottom: 5px; }
.front-advanced .fa-axis input[type="text"] { flex: 0 1 130px; min-width: 80px; }
.front-advanced .fa-axis input[type="range"] { flex: 1 1 160px; }
.front-advanced .fa-axis-t { font-size: 11px; color: #999; width: 36px; text-align: right; }
.front-advanced label.fa-classic { font-size: 12px; color: #666; display: inline-flex;
  gap: 5px; align-items: center; cursor: pointer; }
`;

export function FrontAdvanced(_props: Record<string, never>) {
  const [open, setOpen] = useState(false);
  const [negative, setNegative] = useState("");
  const [axes, setAxes] = useState<Axis[]>([]);
  const [classic, setClassic] = useState(false);

  /** Re-run the current search through the existing 1s debounce. */
  const rerun = (): void => {
    getAction("searchtimed")?.();
  };

  const activeAxes = axes.filter((ax) => ax.a.trim() && ax.b.trim() && ax.t !== 0);
  const axesJson = activeAxes.length > 0 ? JSON.stringify(activeAxes) : "";

  const setAxis = (i: number, patch: Partial<Axis>) => {
    setAxes(axes.map((ax, j) => (j === i ? { ...ax, ...patch } : ax)));
  };

  return (
    <div class="front-advanced">
      <style>{CSS}</style>
      {/* bridges read by navigation.ts search() */}
      <input type="hidden" id="lm_adv_negative" value={negative.trim()} />
      <input type="hidden" id="lm_adv_axes" value={axesJson} />
      <input type="hidden" id="lm_adv_classic" value={classic ? "1" : "0"} />

      <span class="fa-toggle" onClick={() => setOpen(!open)}>
        Advanced {open ? "▲" : "▼"}
      </span>
      {open && (
        <div class="fa-panel">
          <div class="fa-row">
            <input
              type="text"
              class="fa-neg"
              placeholder="exclude (e.g. people, text, snow)"
              value={negative}
              onInput={(e) => setNegative((e.target as HTMLInputElement).value)}
              onChange={rerun}
            />
            <label class="fa-classic">
              <input
                type="checkbox"
                checked={classic}
                onChange={(e) => {
                  setClassic((e.target as HTMLInputElement).checked);
                  rerun();
                }}
              />
              classic ranking
            </label>
          </div>
          <div class="fa-row">
            {PRESET_AXES.filter(([a, b]) => !axes.some((ax) => ax.a === a && ax.b === b)).map(
              ([a, b]) => (
                <span
                  key={`${a}-${b}`}
                  class="fa-chip"
                  onClick={() => {
                    if (axes.length < MAX_AXES) setAxes([...axes, { a, b, t: 0 }]);
                  }}
                >
                  {a} ↔ {b}
                </span>
              )
            )}
            {axes.length < MAX_AXES && (
              <span class="fa-chip" onClick={() => setAxes([...axes, { a: "", b: "", t: 0 }])}>
                + custom
              </span>
            )}
          </div>
          {axes.map((ax, i) => (
            <div key={i} class="fa-axis">
              <input
                type="text"
                value={ax.a}
                placeholder="from"
                onInput={(e) => setAxis(i, { a: (e.target as HTMLInputElement).value })}
                onChange={rerun}
              />
              <input
                type="range"
                min="-1"
                max="1"
                step="0.05"
                value={ax.t}
                onInput={(e) => setAxis(i, { t: Number((e.target as HTMLInputElement).value) })}
                onChange={rerun}
              />
              <input
                type="text"
                value={ax.b}
                placeholder="to"
                onInput={(e) => setAxis(i, { b: (e.target as HTMLInputElement).value })}
                onChange={rerun}
              />
              <span class="fa-axis-t">{ax.t.toFixed(2)}</span>
              <span
                class="fa-chip"
                onClick={() => {
                  setAxes(axes.filter((_, j) => j !== i));
                  rerun();
                }}
              >
                ×
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
